<template>
  <div class="profile__item-container">
    <div
      id="mfa"
      :class="['profile__item', canEdit && 'profile__item--opened']"
      @click="toggleUpdateSection()"
    >
      <div class="profile__icon-wrapper">
        <LockIcon size="1x" class="profile__icon" />
      </div>
      <div class="profile__item-data-wrapper">
        <p class="profile__item-lead">Multi-factor authentication</p>
        <p :class="['profile__item-data', `edit-mfa__item-data--${mfaMethod}`]">
          {{ mfaMethod }}
        </p>
      </div>
      <Edit2Icon
        :class="[
          'dashboard__action-icon',
          canEdit && 'dashboard__action-icon--hide',
        ]"
      />
    </div>
    <transition name="longfade">
      <div
        v-if="isUpdatingSectionOpened"
        class="profile__item-edit-wrapper edit-mfa__wrapper"
      >
        <div class="edit-mfa__title-wrapper">
          <h6 class="edit-mfa__title">Select Method</h6>
        </div>
        <div class="profile__item__form-wrapper">
          <div
            v-for="method in mfaOptions"
            :key="`mfa-method-${method.value}`"
            class="dashboard-form-row"
            @click="selectMFAMethod(method.value)"
          >
            <span class="keyboard-checkbox__input dashboard-form-input">
              <input
                :class="[isSelected(method) && 'keyboard-checkbox--checked']"
                type="radio"
                :name="`${method.value}-checkbox`"
                :value="method.value"
                v-model="selectedMFAMethod"
              />
              <span
                :class="
                  `keyboard-checkbox__control keyboard-checkbox__control--radio`
                "
              >
                <span class="keyboard-checkbox__radio-dot"
              /></span>
            </span>
            <label class="keyboard-checkbox dashboard-form-checkbox">
              <div class="keyboard-checkbox__text">
                <span class="dashboard-form-input-label"
                  >{{ method.name }} {{ method.user_data }}
                </span>
              </div>
            </label>
          </div>
        </div>
        <div class="edit-mfa__bottom-wrapper">
          <div v-if="selectedMFAMethod" class="edit-mfa__alert-wrapper">
            <p class="edit-mfa__alert">
              <InfoIcon />
              <span>You will need access to your selected method to login</span>
            </p>
          </div>
          <div class="profile__item-btn-wrapper edit-mfa__btn-wrapper">
            <button
              v-if="!isLoading"
              @click="updateMFAMethod()"
              class="btn btn--primary profile__item-edit-btn"
              :disabled="isSaveDisabled"
            >
              Save
            </button>
            <button
              v-if="!isLoading"
              @click="cancel()"
              class="btn btn--tertiary profile__item-edit-btn"
            >
              Cancel
            </button>
            <ActionLoader v-else name="ball-clip-rotate" />
          </div>
        </div>
      </div>
    </transition>
    <GoogleAuthenticadorModal
      v-if="isGAModalOpened"
      :gaQrCode="googleAuthenticatorQRCode"
      @close-ga-modal="closeGAModal()"
    />
  </div>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'
import GoogleAuthenticadorModal from '@/components/modals/google-authenticator/GoogleAuthenticatorModal.vue'
import { LockIcon, Edit2Icon, InfoIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'

export default {
  name: 'MFAMethod',
  components: {
    LockIcon,
    Edit2Icon,
    ActionLoader,
    InfoIcon,
    GoogleAuthenticadorModal,
  },
  props: {
    userInfo: {
      type: Object,
      required: true,
    },
    canManageMFA: {
      type: Boolean,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default() {
        return false
      },
    },
  },
  data() {
    return {
      selectedMFAMethod: '',
      isLoading: false,
      editData: false,
      isSaveDisabled: true,
      isGAModalOpened: false,
      googleAuthenticatorQRCode: '',
    }
  },
  watch: {
    selectedMFAMethod: {
      handler: 'updateSelectedMFAMethod',
    },
  },
  computed: {
    mfaOptions() {
      const mfaOptions = [
        {
          name: 'Email: ',
          user_data: this.email,
          value: 'email',
        },
        {
          name: 'SMS: ',
          user_data: this.phone,
          value: 'sms',
        },
        {
          name: 'Google Authenticator',
          user_data: this.google_authenticator,
          value: 'google_auth',
        },
      ]
      if (this.mfaMethod && this.mfaMethod !== 'None' && this.canManageMFA) {
        return [
          ...mfaOptions,
          {
            name: 'Disable MFA',
            value: null,
          },
        ]
      }
      return mfaOptions
    },
    email() {
      const { email } = this.userInfo
      return email
    },
    phone() {
      const { phoneNumber } = this.userInfo
      return phoneNumber || 'None'
    },
    mfaMethod() {
      const { mfaMethod } = this.userInfo
      if (mfaMethod === CONSTANTS.PROFILE.MFA.GOOGLE_AUTHENTICATOR)
        return 'Google Authenticator'
      return mfaMethod || 'None'
    },
    isEditMFAButtonShown() {
      if (this.isBusinessUser && !this.canManageMFA) {
        return false
      }
      return true
    },
    isUpdatingSectionOpened() {
      return this.canEdit && this.editData
    },
  },
  methods: {
    async updateMFAMethod() {
      const { selectedMFAMethod: method } = this
      if (typeof method === 'string' && method === '') {
        this.$store.dispatch('app/setError', 'Please Make a Selection')
      } else {
        this.isLoading = true
        try {
          const response = await this.$store.dispatch(
            'auth/updateMFAMethod',
            method
          )
          this.updateSelectedMFAMethod()
          if (response?.google_mfa_qr_code) {
            this.googleAuthenticatorQRCode = response.google_mfa_qr_code
            this.isGAModalOpened = true
            this.isSaveDisabled = true
          }
        } catch (error) {
          this.isLoading = false
        }
        this.isLoading = false
      }
    },
    updateSelectedMFAMethod() {
      if (this.isUpdatingSectionOpened) this.isSaveDisabled = false
      if (!this.selectedMFAMethod && !this.userInfo?.mfaMethod)
        this.isSaveDisabled = true
    },
    isSelected(method) {
      return method.value === this.selectedMFAMethod
    },
    toggleUpdateSection() {
      this.$emit('itemActive', 'mfa')
      this.editData = !this.isUpdatingSectionOpened
      if (this.isUpdatingSectionOpened) this.$emit('itemActive', '')
    },
    cancel() {
      this.selectedMFAMethod = this.userInfo?.mfaMethod
      this.isSaveDisabled = true
      this.toggleUpdateSection()
    },
    closeGAModal() {
      this.isSaveDisabled = true
      this.isGAModalOpened = false
    },
    selectMFAMethod(method) {
      this.selectedMFAMethod = method
    },
  },
  created() {
    this.selectedMFAMethod = this.userInfo?.mfaMethod
  },
}
</script>

<style lang="scss" scoped>
.edit-mfa {
  &__wrapper {
    @include mobile {
      display: flex;
      flex-direction: column;
    }
  }
  &__title {
    color: var(--white);
    font-size: rem(14);
    line-height: 20px;
    font-weight: 500;
    margin: 0 0 10px 0;
  }
  &__details {
    margin: 0 auto;
    width: 100%;
    &--alert {
      color: var(--white);
      font-family: var(--font-bold);
      text-transform: uppercase;
      width: 100%;
      white-space: nowrap;
    }
  }
  &__alert-wrapper {
    margin: 20px 0 0 0;
    width: 100%;
    @include mobile {
      margin: 0;
    }
  }
  &__alert {
    display: flex;
    align-items: center;
    color: var(--carnation);
    font-size: rem(14);
    line-height: 20px;
    margin: 0;
    svg {
      min-width: 24px;
    }
    span {
      margin-left: 15px;
    }
  }
  &__bottom-wrapper {
    @include mobile {
      display: flex;
      align-items: center;
      margin-top: -7px;
    }
  }
  &__btn-wrapper {
    @include mobile {
      margin-top: 0 !important;
      margin: 0 0 0 auto;
    }
  }
  &__item-data {
    &--sms {
      text-transform: uppercase;
    }
    &--email {
      text-transform: capitalize;
    }
  }
}
</style>
